/**
 * Last.fm API Service
 * Handles all Last.fm API calls and data transformations
 */

const LASTFM_API_KEY = process.env.REACT_APP_LASTFM_API_KEY || 'YOUR_LASTFM_API_KEY';
const LASTFM_BASE_URL = 'https://ws.audioscrobbler.com/2.0/';
const CORS_PROXY = 'https://api.allorigins.win/raw?url=';

/**
 * Fetch user's top tracks from Last.fm
 * @param {string} username - Last.fm username
 * @param {number} limit - Number of tracks to fetch (default: 50)
 * @param {string} period - Time period ('7day', '1month', '3month', '6month', '12month', 'overall')
 * @returns {Promise<Array>} Array of track objects
 */
export const fetchLastfmTopTracks = async (username, limit = 50, period = 'overall') => {
  try {
    const url = new URL(LASTFM_BASE_URL);
    url.searchParams.append('method', 'user.getTopTracks');
    url.searchParams.append('user', username);
    url.searchParams.append('period', period);
    url.searchParams.append('limit', limit);
    url.searchParams.append('api_key', LASTFM_API_KEY);
    url.searchParams.append('format', 'json');

    // Use CORS proxy to bypass CORS restrictions
    const proxyUrl = CORS_PROXY + encodeURIComponent(url.toString());
    
    const response = await fetch(proxyUrl, {
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Last.fm API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(`Last.fm error: ${data.message}`);
    }

    if (!data.toptracks || !data.toptracks.track) {
      return [];
    }

    // Transform Last.fm data to match Spotify format for compatibility
    const tracks = Array.isArray(data.toptracks.track) 
      ? data.toptracks.track 
      : [data.toptracks.track];

    return tracks.map((track) => ({
      id: track.mbid || `${track.name}-${track.artist.name}`,
      name: track.name,
      artist: track.artist.name,
      artists: [{ name: track.artist.name }],
      playcount: parseInt(track.playcount, 10),
      image: track.image && track.image.length > 0 ? track.image[track.image.length - 1]['#text'] : null,
      url: track.url,
      // For compatibility with Spotify format
      album: {
        images: track.image && track.image.length > 0 ? [{ url: track.image[track.image.length - 1]['#text'] }] : []
      }
    }));
  } catch (error) {
    console.error('Error fetching Last.fm top tracks:', error);
    throw error;
  }
};

/**
 * Get information about a Last.fm user
 * @param {string} username - Last.fm username
 * @returns {Promise<Object>} User object
 */
export const getLastfmUser = async (username) => {
  try {
    const url = new URL(LASTFM_BASE_URL);
    url.searchParams.append('method', 'user.getinfo');
    url.searchParams.append('user', username);
    url.searchParams.append('api_key', LASTFM_API_KEY);
    url.searchParams.append('format', 'json');

    // Use CORS proxy to bypass CORS restrictions
    const proxyUrl = CORS_PROXY + encodeURIComponent(url.toString());
    
    const response = await fetch(proxyUrl, {
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Last.fm API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(`Last.fm error: ${data.message}`);
    }

    return data.user;
  } catch (error) {
    console.error('Error fetching Last.fm user info:', error);
    throw error;
  }
};

const lastfmService = {
  fetchLastfmTopTracks,
  getLastfmUser
};

export default lastfmService;

